import { useIsAdmin } from "./useIsAdmin";
import { useIsTrafficManager } from "./useIsTrafficManager";

/**
 * Returns true if the current user can act on YTG operation data beyond viewing it.
 *
 * Covers two families, both restricted to admin+tm at the gateway:
 * - Write actions: create/launch/retry operations, generate PDFs, upload to Drive,
 *   change Drive folder, ad-hoc score, etc.
 * - Data extraction: CSV / Google Sheet export, PDF zip and individual PDF download,
 *   and (UI-only, no server route to gate) opening a guide in a new tab / copying the
 *   optimization chart.
 *
 * Allowed roles: admin, tm (Traffic Manager).
 * Denied roles: user, viewer — read-only. They keep *in-app viewing* (the inline guide
 * preview and the chart) but can neither mutate nor extract data as files. The gateway
 * enforces this server-side; hiding the buttons here is UX so read-only users do not
 * click through to a 403.
 *
 * Note: DELETE operations require admin only — use {@link useIsAdmin} directly for that.
 * This hook intentionally does NOT include delete (TM cannot delete per the RBAC contract).
 */
export const useCanWriteYtg = (): boolean => {
    // Both hooks are called unconditionally to satisfy react-hooks/rules-of-hooks.
    // A short-circuit `||` would skip the second call when the first is true.
    const isAdmin = useIsAdmin();
    const isTm = useIsTrafficManager();
    return isAdmin || isTm;
};
